/**
 * Компонент управления квалификаторами (Qual)
 * 
 * Функциональность:
 * - Отображение списка квалификаторов
 * - Фильтрация по типу квалификатора
 * - Создание нового квалификатора
 * - Редактирование существующего квалификатора
 * - Удаление квалификатора (soft delete)
 * - Поиск по наименованию и коду
 * - Просмотр количества разделов в квалификаторе
 */

import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PermissionGuard } from "@/components/PermissionGuard";
import { qualsApi, qualTypesApi } from "@/services/kls-api.service";
import type { Qual, QualCreate, QualUpdate, QualType } from "@/types/api-v2";
import { qualSchema, type QualFormData } from "@/lib/validations/kls";
import { Plus, Pencil, Trash2, Search, X, Eye, Upload, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import ImportDialog from "./ImportDialog";
import ExportDialog from "./ExportDialog";
import { ReferencesSectionHeader } from "@/components/admin/ReferencesSectionHeader";

export default function QualManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Qual | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQualTypeId, setSelectedQualTypeId] = useState<number | undefined>(undefined);
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortField, setSortField] = useState<"created_at" | "updated_at">("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [klsCounts, setKlsCounts] = useState<Record<number, number>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    item: Qual | null;
  }>({ isOpen: false, item: null });
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // Форма с валидацией
  const form = useForm<QualFormData>({
    resolver: zodResolver(qualSchema),
    defaultValues: {
      qual_type_id: 0,
      qual_namef: "",
      qual_names: "",
      qual_note: "",
      qual_code: "",
      qual_vers: 1,
    },
  });

  // Загрузка типов квалификаторов для селектора
  const { data: qualTypes = [] } = useQuery<QualType[]>({
    queryKey: ["qual-types"],
    queryFn: () => qualTypesApi.list({ include_deleted: false }),
  });

  // Загрузка списка квалификаторов
  const { data: quals = [], isLoading } = useQuery({
    queryKey: ["quals", showDeleted],
    queryFn: () => qualsApi.list({ include_deleted: showDeleted }),
  });

  // Стабильный ключ по списку квалификаторов, чтобы не зациклить useEffect
  const qualsIdsKey = useMemo(
    () => quals.map((q) => q.qual_id).join(","),
    [quals],
  );

  // Загрузка количества разделов для каждого квалификатора
  useEffect(() => {
    let cancelled = false;

    if (!quals.length) {
      setKlsCounts({});
      return;
    }

    const loadCounts = async () => {
      try {
        const results = await Promise.all(
          quals.map((q) =>
            qualsApi
              .getKlsCount(q.qual_id)
              .then((res) => res.count)
              .catch(() => 0),
          ),
        );

        if (cancelled) return;

        const map: Record<number, number> = {};
        quals.forEach((q, index) => {
          map[q.qual_id] = results[index] ?? 0;
        });
        setKlsCounts(map);
      } catch {
        if (!cancelled) {
          // В случае ошибки просто не показываем количество, без падения UI
          setKlsCounts({});
        }
      }
    };

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [qualsIdsKey]);

  // Мутация создания
  const createMutation = useMutation({
    mutationFn: (data: QualCreate) => qualsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quals"] });
      toast({
        title: "Успешно",
        description: "Квалификатор создан",
      });
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось создать квалификатор",
        variant: "destructive",
      });
    },
  });

  // Мутация обновления
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: QualUpdate }) =>
      qualsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quals"] });
      toast({
        title: "Успешно",
        description: "Квалификатор обновлен",
      });
      setIsDialogOpen(false);
      setEditingItem(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось обновить квалификатор",
        variant: "destructive",
      });
    },
  });

  // Мутация удаления
  const deleteMutation = useMutation({
    mutationFn: (id: number) => qualsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quals"] });
      toast({
        title: "Успешно",
        description: "Квалификатор удален",
      });
      setDeleteConfirm({ isOpen: false, item: null });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось удалить квалификатор",
        variant: "destructive",
      });
    },
  });

  // Открытие диалога создания/редактирования
  const openDialog = (item?: Qual) => {
    if (item) {
      setEditingItem(item);
      form.reset({
        qual_type_id: item.qual_type_id,
        qual_namef: item.qual_namef,
        qual_names: item.qual_names || "",
        qual_note: item.qual_note || "",
        qual_code: item.qual_code,
        qual_vers: item.qual_vers,
      });
    } else {
      setEditingItem(null);
      form.reset({
        qual_type_id: 0,
        qual_namef: "",
        qual_names: "",
        qual_note: "",
        qual_code: "",
        qual_vers: 1,
      });
    }
    setIsDialogOpen(true);
  };

  // Обработка отправки формы
  const onSubmit = (data: QualFormData) => {
    if (editingItem) {
      updateMutation.mutate({
        id: editingItem.qual_id,
        data: {
          qual_type_id: data.qual_type_id,
          qual_namef: data.qual_namef,
          qual_names: data.qual_names || undefined,
          qual_note: data.qual_note || undefined,
          qual_code: data.qual_code,
          qual_vers: data.qual_vers,
        },
      });
    } else {
      createMutation.mutate({
        qual_type_id: data.qual_type_id,
        qual_namef: data.qual_namef,
        qual_names: data.qual_names || undefined,
        qual_note: data.qual_note || undefined,
        qual_code: data.qual_code,
        qual_vers: data.qual_vers,
      });
    }
  };

  // Фильтрация и сортировка
  const filteredQuals = useMemo(() => {
    let filtered = quals.filter((item) => {
      if (item.qual_is_del && !showDeleted) return false;
      if (selectedQualTypeId && item.qual_type_id !== selectedQualTypeId) return false;
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item.qual_namef.toLowerCase().includes(query) ||
        item.qual_names?.toLowerCase().includes(query) ||
        item.qual_code.toLowerCase().includes(query)
      );
    });

    // Сортировка по датам
    filtered = [...filtered].sort((a, b) => {
      const aDate = sortField === "created_at" ? a.created_at : a.updated_at || a.created_at;
      const bDate = sortField === "created_at" ? b.created_at : b.updated_at || b.created_at;
      if (!aDate || !bDate) return 0;
      const aTime = new Date(aDate).getTime();
      const bTime = new Date(bDate).getTime();
      return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
    });

    return filtered;
  }, [quals, searchQuery, selectedQualTypeId, showDeleted, sortField, sortDirection]);

  // Обработчик клика по заголовку сортировки
  const handleSortClick = (field: "created_at" | "updated_at") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  return (
    <div className="space-y-4">
      <ReferencesSectionHeader
        title="Квалификаторы"
        description="Справочник квалификаторов KLS: у каждого свой числовой qual_id и набор разделов на вкладке «Разделы классификаторов»."
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExportDialogOpen(true)}
            >
              <Download className="mr-2 h-4 w-4" />
              Экспорт
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsImportDialogOpen(true)}
            >
              <Upload className="mr-2 h-4 w-4" />
              Импорт
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleted(!showDeleted)}
            >
              {showDeleted ? "Скрыть удаленные" : "Показать удаленные"}
            </Button>
            <Button onClick={() => openDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              Добавить квалификатор
            </Button>
          </>
        }
      />

      {/* Фильтры */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по наименованию или коду..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              onClick={() => setSearchQuery("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Combobox
          options={[
            { value: "all", label: "Все типы квалификаторов" },
            ...qualTypes.map((type) => ({
              value: type.qual_type_id.toString(),
              label: type.qual_type_namef,
            })),
          ]}
          value={selectedQualTypeId?.toString() || "all"}
          onValueChange={(value) =>
            setSelectedQualTypeId(value === "all" || !value ? undefined : parseInt(value))
          }
          placeholder="Все типы квалификаторов"
          searchPlaceholder="Поиск типа квалификатора..."
          emptyText="Типы квалификаторов не найдены"
          className="w-[250px]"
          allowClear={true}
          clearText="Все типы квалификаторов"
        />
      </div>

      {/* Таблица */}
      <div className="border rounded-md overflow-hidden">
        <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Тип квалификатора</TableHead>
                <TableHead>Полное наименование</TableHead>
                <TableHead>Сокращенное наименование</TableHead>
                <TableHead>Код</TableHead>
                <TableHead>Версия</TableHead>
                <TableHead>Разделов</TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-medium hover:text-foreground"
                    onClick={() => handleSortClick("created_at")}
                  >
                    Создан
                    {sortField === "created_at" && (
                      <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                    )}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-medium hover:text-foreground"
                    onClick={() => handleSortClick("updated_at")}
                  >
                    Обновлён
                    {sortField === "updated_at" && (
                      <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                    )}
                  </button>
                </TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-sm text-muted-foreground py-6">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : filteredQuals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-sm text-muted-foreground py-6">
                    {searchQuery || selectedQualTypeId
                      ? "Ничего не найдено"
                      : "Квалификаторы отсутствуют"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredQuals.map((item) => (
                  <TableRow key={item.qual_id}>
                    <TableCell>{item.qual_id}</TableCell>
                    <TableCell>
                      {qualTypes.find((t) => t.qual_type_id === item.qual_type_id)?.qual_type_namef ||
                        "-"}
                    </TableCell>
                    <TableCell className="font-medium">{item.qual_namef}</TableCell>
                    <TableCell>{item.qual_names || "-"}</TableCell>
                    <TableCell>{item.qual_code}</TableCell>
                    <TableCell>{item.qual_vers}</TableCell>
                    <TableCell>{klsCounts[item.qual_id] ?? "—"}</TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={item.created_at ? new Date(item.created_at).toLocaleString("ru-RU") : "Нет данных"}
                    >
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString("ru-RU")
                        : "—"}
                    </TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={
                        item.updated_at
                          ? new Date(item.updated_at).toLocaleString("ru-RU")
                          : "Нет обновлений"
                      }
                    >
                      {item.updated_at
                        ? new Date(item.updated_at).toLocaleDateString("ru-RU")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {item.qual_is_del ? (
                        <Badge variant="destructive">Удален</Badge>
                      ) : (
                        <Badge variant="default">Активен</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // TODO: Переход к разделам квалификатора
                            toast({
                              title: "Информация",
                              description: "Просмотр разделов будет реализован в компоненте KlsManagement",
                            });
                          }}
                          disabled={item.qual_is_del}
                          title="Просмотр разделов"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <PermissionGuard permission="kls.quals.edit">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog(item)}
                            disabled={item.qual_is_del}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="kls.quals.delete">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm({ isOpen: true, item })}
                            disabled={item.qual_is_del}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Диалог создания/редактирования */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Редактировать квалификатор" : "Создать квалификатор"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="qual_type_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Тип квалификатора *</FormLabel>
                    <Select
                      value={field.value?.toString()}
                      onValueChange={(value) => field.onChange(parseInt(value))}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Выберите тип квалификатора" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {qualTypes.map((type) => (
                          <SelectItem key={type.qual_type_id} value={type.qual_type_id.toString()}>
                            {type.qual_type_namef}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="qual_namef"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Полное наименование *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="ОКАТО" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="qual_names"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Сокращенное наименование</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="ОКАТО" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="qual_note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Описание</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Описание справочника" rows={3} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="qual_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Код справочника *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="OKATO" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="qual_vers"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Версия</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          min={1}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    form.reset();
                    setEditingItem(null);
                  }}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingItem ? "Сохранить" : "Создать"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, item: null })}
        onConfirm={() => {
          if (deleteConfirm.item) {
            deleteMutation.mutate(deleteConfirm.item.qual_id);
          }
        }}
        title="Удалить квалификатор?"
        description={`Вы уверены, что хотите удалить квалификатор "${deleteConfirm.item?.qual_namef}"?`}
        confirmText="Удалить"
        cancelText="Отмена"
        variant="destructive"
      />
      
      {/* Диалоги импорта/экспорта */}
      <ImportDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        entityType="qual"
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["quals"] });
        }}
      />
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        entityType="qual"
        qualTypeId={selectedQualTypeId}
      />
    </div>
  );
}
